import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";

interface ExecuteOrderModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  order: any;
  onExecuteOrder: (status: any) => void;
}

const ExecuteOrderModal = ({
  open,
  setOpen,
  order,
  onExecuteOrder,
}: ExecuteOrderModalProps) => {
  const [status, setStatus] = useState({
    isPaymentDone: false,
    isSharesTransferred: false,
    transactionDate: "",
    transactionId: "",
    shareTransferredDate: "",
    shareTransferredQuantity: "",
    partialPaymentAmount: "",
    isCompleted: 0,
  });

  console.log(order, "order");

  // Prefill fields when modal opens with existing partial order data
  useEffect(() => {
    if (open && order) {
      setStatus({
        isPaymentDone: order.isPaymentDone === 1 ? true : false,
        isSharesTransferred: order.isSharesTransferred === 1 ? true : false,
        transactionDate: order.latestTransactionDate ? new Date(order.latestTransactionDate).toISOString().split('T')[0] : "",
        transactionId: order.latestTransactionId || "",
        shareTransferredDate: order.latestShareTransferredDate ? new Date(order.latestShareTransferredDate).toISOString().split('T')[0] : "",
        shareTransferredQuantity: order.shareTransferredQuantity || "",
        partialPaymentAmount: order.paidAmount || "",
        isCompleted: 0,
      });
    }
  }, [open, order]);

  const handleExecute = () => {
    // Calculate final values based on completion status
    const brokerageVal = Number(order?.brokerage || 0);
    const finalPaymentAmount = status.isPaymentDone
      ? order?.type === "BUY"
        ? (order?.rate - brokerageVal) * order?.quantity - order?.tcs + order?.stampCharges
        : (order?.rate + brokerageVal) * order?.quantity + order?.stampCharges
      : status.partialPaymentAmount;

    const finalShareQuantity = status.isSharesTransferred
      ? order?.quantity
      : status.shareTransferredQuantity;

    const completedStatus = {
      ...status,
      isCompleted: 1,
      partialPaymentAmount: finalPaymentAmount,
      shareTransferredQuantity: finalShareQuantity,
    };
    onExecuteOrder(completedStatus);
    handleClose();
  };

  const handleReject = () => {
    const rejectedStatus = { ...status, isCompleted: 0 };
    onExecuteOrder(rejectedStatus);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setStatus({
      isPaymentDone: false,
      isSharesTransferred: false,
      transactionDate: "",
      transactionId: "",
      shareTransferredDate: "",
      shareTransferredQuantity: "",
      partialPaymentAmount: "",
      isCompleted: 0,
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        className="bg-white p-6 rounded-xl shadow-lg"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Execute Order</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Order Summary Table */}
        <Table size="small" className="mb-6">
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>Stock</strong>
              </TableCell>
              <TableCell>{order?.stockName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Investor</strong>
              </TableCell>
              <TableCell>{order?.investorName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Type</strong>
              </TableCell>
              <TableCell>{order?.type}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Qty</strong>
              </TableCell>
              <TableCell>{order?.quantity}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Rate</strong>
              </TableCell>
              <TableCell>{order?.rate}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Brokerage</strong>
              </TableCell>
              <TableCell>{order?.brokerage}</TableCell>
            </TableRow> 
            <TableRow>
              <TableCell>
                <strong>TDS</strong>
              </TableCell>
              <TableCell>{order?.tcs}</TableCell>
            </TableRow> 
            <TableRow>
              <TableCell>
                <strong>Stamp Duty</strong>
              </TableCell>
              <TableCell>{order?.stampCharges}</TableCell>
            </TableRow>                                    
            <TableRow>
              <TableCell>
                <strong>Payment Amount</strong>
              </TableCell>
              <TableCell>
                {order?.netAmount}{/* {order?.type == "BUY"
                  ? order?.quantity * order?.rate -
                    order?.tcs +
                    order?.stampCharges
                  : order?.quantity * order?.rate + order?.stampCharges} */}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Share Transferred Qty</strong>
              </TableCell>
              <TableCell>{order?.shareTransferredQuantity}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Amount Paid</strong>
              </TableCell>
              <TableCell>{order?.paidAmount}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <Typography className="mb-1">
              Complete Payment Received / Transferred?
              <span className="text-red-500"> *</span>
            </Typography>
            <div className="flex gap-4">
              <Button
                variant={status.isPaymentDone ? "contained" : "outlined"}
                className={
                  status.isPaymentDone ? "bg-green-600 text-white" : ""
                }
                onClick={() => {
                  const brokerageVal = Number(order?.brokerage || 0);
                  const totalPaymentAmount =
                    order?.type === "BUY"
                      ? (order?.rate - brokerageVal) * order?.quantity -
                        order?.tcs +
                        order?.stampCharges
                      : (order?.rate + brokerageVal) * order?.quantity + order?.stampCharges;

                  setStatus({
                    ...status,
                    isPaymentDone: true,
                    partialPaymentAmount: totalPaymentAmount,
                  });
                }}
              >
                Yes
              </Button>
              <Button
                variant={
                  status.isPaymentDone === false ? "contained" : "outlined"
                }
                className={
                  status.isPaymentDone === false ? "bg-red-600 text-white" : ""
                }
                onClick={() =>
                  setStatus({
                    ...status,
                    isPaymentDone: false,
                    partialPaymentAmount: order?.paidAmount || "",
                  })
                }
              >
                No
              </Button>
            </div>
          </div>

          {/* Show transferred amount field when payment is No */}
          <div className="flex justify-between items-center">
            <Typography className="mb-1">
              Transferred Amount
              <span className="text-red-500"> *</span>
            </Typography>
            <TextField
              label="Transferred Amount"
              type="number"
              value={status.partialPaymentAmount}
              onChange={(e) =>
                setStatus({ ...status, partialPaymentAmount: e.target.value })
              }
              disabled={status.isPaymentDone === true}
            />
          </div>

          <div className="flex justify-between items-center">
            <Typography className="mb-1">
              Complete Shares Received / Transferred?
              <span className="text-red-500"> *</span>
            </Typography>
            <div className="flex gap-4">
              <Button
                variant={status.isSharesTransferred ? "contained" : "outlined"}
                className={
                  status.isSharesTransferred ? "bg-green-600 text-white" : ""
                }
                onClick={() =>
                  setStatus({
                    ...status,
                    isSharesTransferred: true,
                    shareTransferredQuantity: order?.quantity,
                  })
                }
              >
                Yes
              </Button>
              <Button
                variant={
                  status.isSharesTransferred === false
                    ? "contained"
                    : "outlined"
                }
                className={
                  status.isSharesTransferred === false
                    ? "bg-red-600 text-white"
                    : ""
                }
                onClick={() =>
                  setStatus({
                    ...status,
                    isSharesTransferred: false,
                    shareTransferredQuantity: order?.shareTransferredQuantity || "",
                  })
                }
              >
                No
              </Button>
            </div>
          </div>

          {/* share transfer quantity - always show but required when shares is No */}
          <div className="flex justify-between items-center">
            <Typography className="mb-1">
              Share Received / Transferred Quantity
              <span className="text-red-500"> *</span>
            </Typography>
            <TextField
              label="Share Transferred Quantity"
              type="number"
              value={status.shareTransferredQuantity}
              onChange={(e) =>
                setStatus({
                  ...status,
                  shareTransferredQuantity: e.target.value,
                })
              }
              disabled={status.isSharesTransferred === true}
            />
          </div>

          {/* payment Date */}
          <div className="flex justify-between items-center">
            <Typography className="mb-1">
              Payment Received / Transferred Date
            </Typography>
            <TextField
              label="Date"
              name="transactionDate"
              type="date"
              value={status.transactionDate}
              onChange={(e) =>
                setStatus({ ...status, transactionDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </div>

          {/* share transfer Date */}
          <div className="flex justify-between items-center">
            <Typography className="mb-1">
              Share Received / Transferred Date
            </Typography>
            <TextField
              label="Date"
              name="shareTransferredDate"
              type="date"
              value={status.shareTransferredDate}
              onChange={(e) =>
                setStatus({ ...status, shareTransferredDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </div>

          {/* payment id */}
          <div className="flex justify-between items-center">
            <Typography className="mb-1">Payment ID</Typography>
            <TextField
              type="text"
              placeholder="Enter Payment ID"
              value={status.transactionId}
              onChange={(e) =>
                setStatus({ ...status, transactionId: e.target.value })
              }
            />
          </div>
        </div>

        {/* Action Buttons */}
        <Box mt={6} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleReject}
            className="text-red-600 border-red-600 hover:bg-red-50"
          >
            Reject Order
          </Button>
          <Button
            variant="contained"
            className="bg-blue-600 text-white"
            onClick={handleExecute}
            disabled={
              // For complete orders: both payment and shares must be done
              status.isPaymentDone === true &&
              status.isSharesTransferred === true
                ? false
                : // For partial orders: check if required fields are filled
                  (status.isPaymentDone === false &&
                    !status.partialPaymentAmount) ||
                  (status.isSharesTransferred === false &&
                    !status.shareTransferredQuantity)
            }
          >
            {status.isPaymentDone === false ||
            status.isSharesTransferred === false
              ? "Partially Complete Order"
              : "Complete Order"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExecuteOrderModal;
